import { ApolloError } from '@apollo/client';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { useViewer } from '@treams/treams-hooks';
import { useLocalizedGuideUrl } from '@treams/treams-hooks';
import { useVerifyTotpTokenMutation } from '@treams/treams-network';
import {
	Button,
	ClipboardButton,
	FlexGap,
	Modal,
	OtpInput,
	Paragraph,
	Popover,
	Subheading,
	UnderlineLink,
	tokens,
} from '@treams/treams-ui';

export default function SecretKeyVerification(props: {
	secretKey: string;
	onDismiss: () => void;
	onComplete: (session?: string) => void;
	challenge?: {
		session: string;
		username: string;
	};
}) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const guideUrl = useLocalizedGuideUrl({ en: '2fa-176bgjl', nl: '2fa-62fvke' });

	const showSecretKeyModal = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		setVisible(true);
	}, []);

	return (
		<Flex alignItems="flex-start" flexDirection="column">
			<Subheading>{t('personalSettings.mfa.totp.secretKeyVerification.title')}</Subheading>
			<Paragraph>
				<Trans
					i18nKey="personalSettings.mfa.totp.secretKeyVerification.description"
					components={{
						a: <UnderlineLink href={guideUrl} target="_blank" />,
					}}
				/>
			</Paragraph>
			<SecretKeyQrCode secretKey={props.secretKey} username={props.challenge?.username} />
			<Paragraph>
				<Trans
					i18nKey="personalSettings.mfa.totp.secretKeyVerification.unableToScan"
					components={{
						a: <UnderlineLink onClick={showSecretKeyModal} href="#" />,
					}}
				/>
			</Paragraph>
			<SecretKeyVerificationForm
				secretKey={props.secretKey}
				onDismiss={props.onDismiss}
				onComplete={props.onComplete}
				session={props.challenge?.session}
			/>
			<SecretKeyModal
				secretKey={props.secretKey}
				visible={visible}
				onDismiss={() => setVisible(false)}
			/>
		</Flex>
	);
}

function SecretKeyVerificationForm(props: {
	secretKey: string;
	onDismiss: () => void;
	onComplete: (session?: string) => void;
	session?: string;
}) {
	const { t } = useTranslation();

	const [totp, setTotp] = useState('');
	const [error, setError] = useState<string | undefined>(undefined);

	const isValid = useMemo(() => /^(\d(\s+)?){6}$/.test(totp), [totp]);

	const [verifyTotpToken] = useVerifyTotpTokenMutation();

	const onVerify = useCallback(async () => {
		setError(undefined);

		try {
			const result = await verifyTotpToken({
				variables: { input: { totp, session: props.session } },
			});

			if (result.data?.verifyTotpToken.success) {
				props.onComplete(result.data?.verifyTotpToken.session ?? undefined);
			}
		} catch (error: unknown) {
			if (error instanceof ApolloError && error.cause?.extensions) {
				if ('error' in error.cause.extensions) {
					setTotp('');
					setError((error.cause.extensions.error as { key: string }).key);
				}
			}
		}
	}, [verifyTotpToken, totp, props]);

	return (
		<FlexGap gap={8} flexDirection="column" alignItems="flex-start" mt={4}>
			<Subheading>{t('personalSettings.mfa.totp.secretKeyVerification.verifyHeading')}</Subheading>
			<OtpInput length={6} value={totp} onChange={setTotp} autoFocus />
			{error && <Paragraph color="danger">{t(error)}</Paragraph>}
			<FlexGap gap={8} mt={4}>
				<Button
					title={t('personalSettings.mfa.totp.secretKeyVerification.verify')}
					onClick={onVerify}
					small
					disabled={!isValid}
				/>
				<Button type="secondary" title={t('common.cancel')} onClick={props.onDismiss} small />
			</FlexGap>
		</FlexGap>
	);
}

function useSecretKeyUri(secretKey: string, options: { username?: string } = {}) {
	const viewer = useViewer();

	return useMemo(() => {
		const issuer = 'Treams';

		const params = new URLSearchParams();
		params.set('secret', secretKey);
		params.set('issuer', issuer);

		const label = viewer?.email ?? options.username ?? 'account';

		return `otpauth://totp/${issuer}:${label}?${params.toString()}`;
	}, [secretKey, viewer?.email, options.username]);
}

const QrCodeContainer = styled(Box)`
	border: ${`1px solid ${tokens.border.subtle}`};
	border-radius: 8px;
`;

function SecretKeyQrCode(props: { secretKey: string; username?: string }) {
	const secretKeyUri = useSecretKeyUri(props.secretKey, { username: props.username });
	const theme = useTheme();
	const qrBackgroundColor = theme.colors.white;

	return (
		<QrCodeContainer backgroundColor={qrBackgroundColor} p={16} my={16}>
			<QRCodeSVG value={secretKeyUri} bgColor={qrBackgroundColor} fgColor={theme.colors.black} />
		</QrCodeContainer>
	);
}

function SecretKeyModal(props: { secretKey: string; visible: boolean; onDismiss: () => void }) {
	const { t } = useTranslation();

	return (
		<Popover center visible={props.visible} onDismiss={props.onDismiss}>
			<Modal>
				<Modal.Header
					title={t('personalSettings.mfa.totp.secretKeyVerification.secretKey')}
					onClose={props.onDismiss}
				/>
				<Modal.Contents>
					<Flex alignItems="center" justifyContent="space-between">
						<Paragraph>{props.secretKey}</Paragraph>
						<ClipboardButton text={props.secretKey} type="plain" />
					</Flex>
				</Modal.Contents>
			</Modal>
		</Popover>
	);
}
